import { chain } from 'lodash';
import React from 'react';
import Select, { OptionProps } from 'react-select';
import { DefaultFailureMode } from '../../../../common/src/domain/defaultFailureMode/defaultFailureModeTableSchema';
import { FailureMode } from '../../../../common/src/domain/failureMode/failureModeTableSchema';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchGet } from '../../store/fetch';
import { getFailureModeDisplayName } from '../../utils/data/fmecaDataDefinition';
import { InspectionStandardTypes } from '../../utils/enums/InspectionStandardTypes';
import { getAssetThumbnailUrl } from '../../utils/getAssetThumbnailUrl';
import { isEemuaOrApi } from '../../utils/isEemuaOrApi';
import { reactSelectDefaultStyles } from '../../utils/reactSelectDefaultStyles';
import { IElement } from '../Surveys/Interfaces/IElement';

type FailureModeView = Pick<FailureMode, 'id' | 'customName' | 'manifestationId' | 'metaData'> & { assets: IAsset[] } & {
    defaultFailureMode: Pick<DefaultFailureMode, 'id' | 'name'>;
};

interface IAsset {
    id: string;
    name: string;
    type: string;
}

function getUnits(elements: IElement[], elementId: string | null | undefined) {
    const element = elements.find((i) => i.id === elementId);
    if (!element) {
        return [];
    }
    return element.units;
}

function getUnit(elements: IElement[], elementId: string | null | undefined, unitId: string | null | undefined) {
    const units = getUnits(elements, elementId);

    return units.find((i) => i.id === unitId);
}

function getManifestations(elements: IElement[], elementId: string | null | undefined, unitId: string | null | undefined) {
    const unit = getUnit(elements, elementId, unitId);

    if (!unit) {
        return [];
    }
    return unit.manifestations;
}

interface IElementCategory {
    id: string;
    name: string;
}

export function ElementSelection(props: {
    elementFilter?: (element: IElement) => boolean;
    surveyId: string;
    elementId?: string | null | undefined;
    unitId?: string | null | undefined;
    manifestationId?: string | null | undefined;
    assetId?: string | null | undefined;
    failureModeId?: string | null | undefined;
    useAssets?: boolean | undefined;
    useFailureModes?: boolean | undefined;
    ignoreManifestations?: boolean | undefined;
    onChange(value: {
        elementId?: string | null | undefined;
        unitId?: string | null | undefined;
        manifestationId?: string | null | undefined;
        failureModeId?: string | null | undefined;
        assetId?: string | null | undefined;
        elementName?: string | undefined;
        unitName?: string | undefined;
        manifestationName?: string | undefined;
        failureModeName?: string | undefined;
        assetName?: string | undefined;
    }): void;
}) {
    const translate = useTranslate();

    const [failureModes, setFailureModes] = React.useState<FailureModeView[]>([]);

    const [elements, setElements] = React.useState([] as IElement[]);
    const [survey, setSurvey] = React.useState(
        null as {
            inspectionStandardType: InspectionStandardTypes;
            objectId: string;
        } | null,
    );
    const [selectedCategoryId, setSelectedCategoryId] = React.useState<string | null>(null);
    const [categories, setCategories] = React.useState(
        [] as {
            id: string;
            name: string;
        }[],
    );

    React.useEffect(() => {
        const fetchData = async () => {
            if (props.surveyId) {
                const surveyResponse = await fetchGet(`/api/surveys/${props.surveyId}`);
                if (surveyResponse.json) {
                    setSurvey(surveyResponse.json);
                    const elementsResponse = await fetchGet(`/api/objects/${surveyResponse.json.objectId}/elements`, {
                        surveyId: props.surveyId,
                        includeAssets: props.useAssets ? 'true' : 'false',
                    });
                    let foundElements = elementsResponse.json as IElement[];
                    if (
                        props.elementFilter &&
                        (surveyResponse.json.inspectionStandardType === InspectionStandardTypes.Eemua159 ||
                            surveyResponse.json.inspectionStandardType === InspectionStandardTypes.Api653)
                    ) {
                        foundElements = foundElements.filter(props.elementFilter);
                    }
                    setElements(foundElements);
                    const distinctCategories = chain(foundElements)
                        .uniqBy((i) => i.categoryId)
                        .map((i) => ({ id: i.categoryId, name: i.categoryName }))
                        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
                        .value();

                    setCategories(distinctCategories as IElementCategory[]);
                }
            }
        };
        void fetchData();
    }, [props.surveyId, props.useAssets, props.elementFilter]);

    React.useEffect(() => {
        async function loadFailureModes() {
            if (!props.useFailureModes) {
                return;
            }
            if (!props.unitId && !props.manifestationId) {
                setFailureModes([]);
                return;
            }
            const response = await fetchGet(`/api/surveys/${props.surveyId}/failureModes`, {
                unitId: props.unitId,
                manifestationId: props.manifestationId === null ? undefined : props.manifestationId,
                includeAssets: props.useAssets ? 'true' : 'false',
            });
            let responseFailureModes: FailureModeView[] = response.json;
            if (!props.manifestationId) {
                responseFailureModes = responseFailureModes.filter((i) => i.manifestationId === null);
            }
            setFailureModes(responseFailureModes);
        }
        void loadFailureModes();
    }, [props.elementId, props.unitId, props.manifestationId, props.surveyId, props.useAssets, props.useFailureModes]);

    function handleElementChange(elementId: string) {
        const elementName = elements.find((i) => i.id === elementId)?.name;
        props.onChange({
            elementId: elementId ? elementId : null,
            elementName,
            unitId: null,
            manifestationId: null,
            failureModeId: props.useFailureModes ? null : undefined,
            assetId: props.useAssets ? null : undefined,
        });
    }
    function handleUnitChange(unitId: string) {
        const elementName = elements.find((i) => i.id === props.elementId)?.name;
        const unit = getUnit(elements, props.elementId, unitId);
        const unitName = unit ? unit.name : undefined;
        props.onChange({
            elementId: props.elementId,
            elementName,
            unitId: unitId ? unitId : null,
            unitName,
            manifestationId: null,
            failureModeId: props.useFailureModes ? null : undefined,
            assetId: props.useAssets ? null : undefined,
        });
    }
    function handleManifestationChange(manifestationId: string) {
        const elementName = elements.find((i) => i.id === props.elementId)?.name;
        const unit = getUnit(elements, props.elementId, props.unitId);
        const unitName = unit ? unit.name : undefined;

        const manifestation = getManifestations(elements, props.elementId, props.unitId).find((i) => i.id === manifestationId);
        const manifestationName = manifestation ? manifestation.name : undefined;
        props.onChange({
            elementId: props.elementId,
            elementName,
            unitId: props.unitId,
            unitName,
            manifestationId: manifestationId ? manifestationId : null,
            manifestationName,
            failureModeId: props.useFailureModes ? null : undefined,
            assetId: props.useAssets ? null : undefined,
        });
    }
    function handleCategoryChange(value: string) {
        setSelectedCategoryId(value);
    }
    function handleFailureModeChange(updatedFailureModeId?: string) {
        const elementName = elements.find((i) => i.id === props.elementId)?.name;
        const unit = getUnit(elements, props.elementId, props.unitId);
        const unitName = unit ? unit.name : undefined;
        const manifestation = getManifestations(elements, props.elementId, props.unitId).find((i) => i.id === props.manifestationId);
        const manifestationName = manifestation ? manifestation.name : undefined;

        const failureMode = failureModes.find((i) => i.id === updatedFailureModeId);
        const failureModeName = failureMode ? getFailureModeNameForTitle(failureMode) : undefined;
        props.onChange({
            elementId: props.elementId,
            elementName,
            unitId: props.unitId,
            unitName,
            manifestationId: props.manifestationId,
            manifestationName,
            assetId: props.assetId,
            failureModeId: updatedFailureModeId ? updatedFailureModeId : null,
            failureModeName,
        });
    }
    function handleAssetChange(updatedAssetId?: string) {
        const elementName = elements.find((i) => i.id === props.elementId)?.name;
        const unit = getUnit(elements, props.elementId, props.unitId);
        const unitName = unit ? unit.name : undefined;
        const manifestation = getManifestations(elements, props.elementId, props.unitId).find((i) => i.id === props.manifestationId);
        const manifestationName = manifestation ? manifestation.name : undefined;
        const failureMode = failureModes.find((i) => i.id === props.failureModeId);
        const failureModeName = failureMode ? getFailureModeNameForTitle(failureMode) : undefined;

        const assets = getAssets({
            elementId: props.elementId,
            unitId: props.unitId,
            manifestationId: props.manifestationId,
            failureModeIdForAssets: props.failureModeId,
        });
        const asset = assets?.find((i) => i.id === updatedAssetId);
        const assetName = asset ? asset.name : undefined;
        props.onChange({
            elementId: props.elementId,
            elementName,
            unitId: props.unitId,
            unitName,
            manifestationId: props.manifestationId,
            manifestationName,
            failureModeId: props.failureModeId,
            failureModeName,
            assetId: updatedAssetId ? updatedAssetId : null,
            assetName,
        });
    }

    function getAssets({
        elementId,
        unitId,
        manifestationId,
        failureModeIdForAssets,
    }: {
        elementId?: undefined | string | null;
        unitId?: undefined | string | null;
        manifestationId?: undefined | string | null;
        failureModeIdForAssets?: undefined | string | null;
    }) {
        if (failureModeIdForAssets) {
            const failureMode = failureModes.find((i) => i.id === failureModeIdForAssets);
            if (failureMode) {
                return failureMode.assets;
            }
            return [];
        }

        if (manifestationId) {
            const selectedElement = elements.find((i) => i.id === elementId);
            if (selectedElement) {
                const selectedUnit = selectedElement.units.find((i) => i.id === unitId);
                if (selectedUnit) {
                    const selectedManifestation = selectedUnit.manifestations.find((i) => i.id === manifestationId);
                    if (selectedManifestation) {
                        return selectedManifestation.assets;
                    }
                }
            }
        } else if (unitId) {
            const selectedElement = elements.find((i) => i.id === elementId);
            if (selectedElement) {
                const selectedUnit = selectedElement.units.find((i) => i.id === unitId);
                if (selectedUnit) {
                    return selectedUnit.assets;
                }
            }
        } else if (elementId) {
            const selectedElement = elements.find((i) => i.id === elementId);
            if (selectedElement) {
                return selectedElement.assets;
            }
        }
        return [];
    }

    function getFailureModeNameForTitle(failureModeToDisplay: FailureModeView) {
        return `${
            failureModeToDisplay.defaultFailureMode.name
                ? failureModeToDisplay.defaultFailureMode.name
                : failureModeToDisplay.customName
                  ? failureModeToDisplay.customName
                  : ''
        }`;
    }

    const filteredElements = selectedCategoryId ? elements.filter((i) => i.categoryId === selectedCategoryId) : elements;
    if (!survey) {
        return null;
    }
    const areCategoriesVisible = isEemuaOrApi(survey.inspectionStandardType);

    const assets = props.useAssets
        ? (getAssets({
              elementId: props.elementId,
              unitId: props.unitId,
              manifestationId: props.manifestationId,
              failureModeIdForAssets: props.failureModeId,
          }) ?? [])
        : [];

    return (
        <>
            {areCategoriesVisible ? (
                <>
                    <label className="select field mb5" style={{ width: '320px' }}>
                        <FilterSelect
                            placeholder={translate('common.category')}
                            value={selectedCategoryId}
                            data={categories}
                            mapRowToOption={(i) => ({ value: i.id, label: i.name })}
                            onChange={(value) => handleCategoryChange(value)}
                        />
                    </label>
                    <br />

                    {selectedCategoryId && (
                        <label className="select field mb5" style={{ width: '320px' }}>
                            <FilterSelect
                                placeholder={translate('common.element')}
                                value={props.elementId}
                                data={filteredElements}
                                mapRowToOption={(i) => ({ value: i.id, label: i.name })}
                                onChange={(value) => handleElementChange(value)}
                            />
                        </label>
                    )}
                    {props.elementId && getUnits(elements, props.elementId).length > 0 && (
                        <div>
                            <label className="select field mb5" style={{ width: '320px' }}>
                                <FilterSelect
                                    placeholder={translate('common.unit')}
                                    value={props.unitId}
                                    data={getUnits(elements, props.elementId)}
                                    mapRowToOption={(i) => ({ value: i.id, label: i.name, isArchived: i.isArchived })}
                                    onChange={(value) => handleUnitChange(value)}
                                />
                            </label>
                        </div>
                    )}
                </>
            ) : (
                <>
                    <div>
                        <label className="select field mb5" style={{ width: '320px' }}>
                            <FilterSelect
                                placeholder={translate('common.element')}
                                value={props.elementId}
                                data={filteredElements}
                                mapRowToOption={(i) => ({ value: i.id, label: i.name, isArchived: i.isArchived })}
                                onChange={(value) => handleElementChange(value)}
                            />
                        </label>
                    </div>
                    {props.elementId && getUnits(elements, props.elementId).length > 0 && (
                        <div>
                            <label className="select field mb5" style={{ width: '320px' }}>
                                <FilterSelect
                                    placeholder={translate('common.unit')}
                                    value={props.unitId}
                                    data={getUnits(elements, props.elementId)}
                                    mapRowToOption={(i) => ({ value: i.id, label: i.name, isArchived: i.isArchived })}
                                    onChange={(value) => handleUnitChange(value)}
                                />
                            </label>
                        </div>
                    )}
                    {props.unitId && !props.ignoreManifestations && getManifestations(elements, props.elementId, props.unitId).length > 0 && (
                        <div>
                            <label className="select field mb5" style={{ width: '320px' }}>
                                <FilterSelect
                                    placeholder={translate('common.manifestation')}
                                    value={props.manifestationId}
                                    data={getManifestations(elements, props.elementId, props.unitId)}
                                    mapRowToOption={(i) => ({ value: i.id, label: i.name })}
                                    onChange={(value) => handleManifestationChange(value)}
                                />
                            </label>
                        </div>
                    )}

                    {props.useFailureModes && failureModes.length > 0 && (
                        <div>
                            <label className="select field mb5" style={{ width: '320px' }}>
                                <FilterSelect
                                    placeholder={translate('failureMode.failureMode')}
                                    value={props.failureModeId}
                                    data={failureModes}
                                    mapRowToOption={(i) => ({ value: i.id, label: getFailureModeDisplayName(i) })}
                                    onChange={(value) => handleFailureModeChange(value)}
                                />
                            </label>
                        </div>
                    )}
                </>
            )}
            {
                /* Asset selection */
                props.useAssets && assets.length > 0 && (
                    <div>
                        <label className="select field mb5" style={{ width: '320px' }}>
                            <ImageSelect
                                placeholder={translate('asset.asset')}
                                value={props.assetId}
                                data={assets}
                                mapRowToOption={(i) => ({ value: i.id, label: i.name })}
                                onChange={(value) => handleAssetChange(value)}
                            />
                        </label>
                    </div>
                )
            }
        </>
    );
}

function FilterSelect<DataRow>(props: {
    value: string | null | undefined;
    placeholder: string;
    data: DataRow[];
    mapRowToOption: (row: DataRow) => { value: string; label: string };
    onChange: (value: string) => unknown;
}) {
    return (
        <Select
            styles={{
                ...reactSelectDefaultStyles,
                singleValue: (styles, state) => ({
                    ...styles,
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                    color: (state.data as any).isArchived ? 'lightgrey' : undefined,
                }),
                option: (styles, state) => ({
                    ...styles,
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                    color: (state.data as any).isArchived ? 'lightgrey' : undefined,
                }),
            }}
            isClearable={true}
            className="zxy-style-select"
            placeholder={props.placeholder}
            value={props.value ? props.data.map((i) => props.mapRowToOption(i)).find((i) => i.value === props.value) : undefined}
            options={props.data.map((i) => props.mapRowToOption(i))}
            onChange={(option: any) => {
                props.onChange(option ? option.value : null);
            }}
        />
    );
}

function ImageSelect(props: {
    value: string | undefined | null;
    placeholder: string;
    data: IAsset[];
    mapRowToOption: (row: IAsset) => { value: string; label: string };
    onChange: (value: string) => unknown;
}) {
    function ImageOption(commonProps: OptionProps<{ value: string; label: string }>) {
        const { getStyles, innerProps } = commonProps;

        const asset = props.data.find((i) => i.id === commonProps.data.value)!;

        return (
            <div style={getStyles('option', commonProps) as any} {...innerProps}>
                <img loading="lazy" src={getAssetThumbnailUrl(asset)} width="40px" /> {asset.name}
            </div>
        );
    }

    return (
        <Select
            isClearable={true}
            className="zxy-style-select"
            // eslint-disable-next-line @typescript-eslint/naming-convention
            components={{ Option: ImageOption }}
            styles={reactSelectDefaultStyles}
            placeholder={props.placeholder}
            value={(props.value ? props.data.map((i) => props.mapRowToOption(i)).find((i) => i.value === props.value) : null) ?? null}
            options={props.data.map((i) => props.mapRowToOption(i))}
            onChange={(option: any) => {
                props.onChange(option ? option.value : null);
            }}
        />
    );
}
